import React, { useCallback, useRef, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import Cropper from "react-easy-crop";

/** Misura esatta dell'immagine prodotta. Il riquadro mostrato ha la stessa forma. */
export const LARGHEZZA_PRODOTTO = 400;
export const ALTEZZA_PRODOTTO = 224;

const MAX_FILE_SIZE = 12 * 1024 * 1024;
const TIPI_AMMESSI = ['image/png', 'image/jpeg', 'image/webp'];
const QUALITA_JPEG = 0.85;

const ZOOM_MIN = 1;
const ZOOM_MAX = 4;

const caricaImmagine = (src) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('immagine illeggibile'));
        img.src = src;
    });

/**
 * Ritaglia alla misura esatta richiesta: `area` arriva dal cropper in pixel
 * dell'immagine originale, la destinazione e` sempre larghezza x altezza.
 */
const ritaglia = async (src, area, larghezza, altezza, formato) => {
    const img = await caricaImmagine(src);

    const canvas = document.createElement('canvas');
    canvas.width = larghezza;
    canvas.height = altezza;

    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingQuality = 'high';

    // Il JPEG non ha canale alpha: senza fondo bianco le zone trasparenti di un
    // PNG di partenza diventerebbero nere. Il PNG invece la trasparenza la
    // conserva, ed e` il motivo per cui lo si sceglie: un logo su fondo
    // trasparente non deve ritrovarsi un riquadro bianco dietro.
    if (formato !== 'png') {
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, larghezza, altezza);
    }

    ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, larghezza, altezza);

    return formato === 'png'
        ? canvas.toDataURL('image/png').split(',')[1]
        : canvas.toDataURL('image/jpeg', QUALITA_JPEG).split(',')[1];
};

const descriviTipoNonAmmesso = (file) => {
    const nome = (file?.name ?? '').toLowerCase();
    // Le foto dell'iPhone sono spesso HEIC e il browser non sa aprirle: senza
    // dirlo, chi carica vede solo un rifiuto e non sa cosa fare.
    if (nome.endsWith('.heic') || nome.endsWith('.heif')) {
        return 'Le foto in formato HEIC dell\'iPhone non si possono caricare. Sul telefono apri Impostazioni > Fotocamera > Formati e scegli "Massima compatibilità", oppure esporta la foto in JPEG.';
    }
    return 'Formato non supportato: servono immagini JPG, PNG o WebP.';
};

/**
 * Caricamento dell'immagine di un prodotto, in una schermata sola.
 *
 * Il ritaglio e` a riquadro fisso e non libero, e l'immagine prodotta esce
 * sempre esattamente a LARGHEZZA_PRODOTTO x ALTEZZA_PRODOTTO: e` l'unico modo
 * perche' chi carica veda in anticipo cio` che l'app mostrera` davvero. Con il
 * ritaglio libero di prima ogni prodotto finiva con una forma diversa, e il
 * riquadro poteva restare vuoto - da cui l'errore "Seleziona un riquadro da
 * ritagliare" a fine procedura, quando ormai il modulo era compilato.
 *
 * Gli errori si leggono qui dentro, accanto al punto in cui si sbaglia, e non
 * bloccano niente: si cambia file e si va avanti.
 *
 * Props:
 * - show, onClose(), onSave(base64 JPEG senza prefisso data URI)
 * - titolo (opzionale)
 * - larghezza / altezza (opzionali): misura dell'immagine prodotta
 * - formato (opzionale): 'jpeg' (default) oppure 'png', per chi ha bisogno
 *     della trasparenza, come i loghi
 * - extra (opzionale): campi aggiuntivi mostrati sotto il ritaglio, gestiti da chi usa il componente
 * - onFileScelto(nome) (opzionale): chiamata col nome del file appena scelto
 * - salvaDisabilitato (opzionale): blocca Salva finche' i campi extra non sono validi
 */
export const ProductImageUploader = ({
    show,
    onClose,
    onSave,
    titolo = "Immagine del prodotto",
    larghezza = LARGHEZZA_PRODOTTO,
    altezza = ALTEZZA_PRODOTTO,
    formato = 'jpeg',
    extra = null,
    onFileScelto,
    salvaDisabilitato = false,
}) => {
    const [sorgente, setSorgente] = useState(null);
    const [nomeFile, setNomeFile] = useState('');
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [area, setArea] = useState(null);
    const [errore, setErrore] = useState('');
    const [trascinamento, setTrascinamento] = useState(false);
    const [salvataggio, setSalvataggio] = useState(false);
    const inputRef = useRef(null);

    const azzera = () => {
        setSorgente(null);
        setNomeFile('');
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setArea(null);
        setErrore('');
        setTrascinamento(false);
        setSalvataggio(false);
    };

    const accetta = (file) => {
        if (!file) return;

        if (!TIPI_AMMESSI.includes(file.type)) {
            setErrore(descriviTipoNonAmmesso(file));
            return;
        }
        if (file.size > MAX_FILE_SIZE) {
            const mb = (file.size / (1024 * 1024)).toFixed(1);
            setErrore(`L'immagine pesa ${mb} MB, il massimo è 12 MB. Scegline una più leggera.`);
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            setSorgente(reader.result);
            setNomeFile(file.name);
            onFileScelto?.(file.name);
            setCrop({ x: 0, y: 0 });
            setZoom(1);
            setArea(null);
            setErrore('');
        };
        reader.onerror = () => setErrore('Non sono riuscito a leggere il file. Riprova con un\'altra immagine.');
        reader.readAsDataURL(file);
    };

    const onCropComplete = useCallback((_, areaInPixel) => setArea(areaInPixel), []);

    const salva = async () => {
        if (!sorgente || !area) return;

        setSalvataggio(true);
        try {
            const base64 = await ritaglia(sorgente, area, larghezza, altezza, formato);
            azzera();
            onSave(base64);
        } catch (e) {
            setSalvataggio(false);
            setErrore('Non sono riuscito a preparare l\'immagine. Riprova con un\'altra.');
        }
    };

    const annulla = () => {
        azzera();
        if (onClose) onClose();
    };

    const onDrop = (event) => {
        event.preventDefault();
        setTrascinamento(false);
        accetta(event.dataTransfer.files?.[0]);
    };

    return (
        <Modal className="fade" show={show} centered onHide={annulla}>
            <Modal.Header>
                <Modal.Title>{titolo}</Modal.Title>
                <Button onClick={annulla} variant="" className="btn-close"></Button>
            </Modal.Header>

            <Modal.Body>
                {!sorgente ? (
                    <div
                        onClick={() => inputRef.current?.click()}
                        onDragOver={(e) => { e.preventDefault(); setTrascinamento(true); }}
                        onDragLeave={() => setTrascinamento(false)}
                        onDrop={onDrop}
                        style={{
                            border: `2px dashed ${trascinamento ? '#886CC0' : '#d1d5db'}`,
                            backgroundColor: trascinamento ? '#f5f2fb' : '#fafafa',
                            borderRadius: 8,
                            padding: '38px 20px',
                            textAlign: 'center',
                            cursor: 'pointer',
                        }}
                    >
                        <div style={{ fontWeight: 600, marginBottom: 6 }}>
                            Trascina qui l'immagine, oppure fai clic per sceglierla
                        </div>
                        <div style={{ fontSize: 13, color: '#6b7280' }}>
                            JPG, PNG o WebP fino a 12 MB. Al passo successivo scegli tu quale parte mostrare.
                        </div>
                    </div>
                ) : (
                    <>
                        <div style={{ position: 'relative', width: '100%', height: 260, background: '#333', borderRadius: 8, overflow: 'hidden' }}>
                            <Cropper
                                image={sorgente}
                                crop={crop}
                                zoom={zoom}
                                minZoom={ZOOM_MIN}
                                maxZoom={ZOOM_MAX}
                                aspect={larghezza / altezza}
                                onCropChange={setCrop}
                                onZoomChange={setZoom}
                                onCropComplete={onCropComplete}
                                objectFit="contain"
                                showGrid={false}
                            />
                        </div>

                        <div className="mt-3">
                            <Form.Label className="mb-1">Zoom</Form.Label>
                            <Form.Range
                                min={ZOOM_MIN}
                                max={ZOOM_MAX}
                                step={0.05}
                                value={zoom}
                                onChange={(e) => setZoom(Number(e.target.value))}
                            />
                        </div>

                        <div className="d-flex justify-content-between align-items-center mt-2" style={{ fontSize: 13, color: '#6b7280' }}>
                            <span>
                                Trascina l'immagine per spostarla. Verrà salvata a {larghezza}×{altezza} pixel.
                            </span>
                            <Button variant="link" className="p-0" onClick={() => inputRef.current?.click()}>
                                Cambia immagine
                            </Button>
                        </div>

                        {nomeFile && (
                            <div className="mt-1" style={{ fontSize: 12, color: '#9ca3af' }}>{nomeFile}</div>
                        )}
                    </>
                )}

                <input
                    ref={inputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                        accetta(e.target.files?.[0]);
                        // Senza questo, riscegliere lo stesso file non emette l'evento.
                        e.target.value = '';
                    }}
                />

                {sorgente && extra}

                {errore && (
                    <div className="mt-3" style={{ color: '#dc3545', fontSize: 13 }}>{errore}</div>
                )}
            </Modal.Body>

            <Modal.Footer className="justify-content-between">
                <Button className="btn btn-light btn-add" onClick={annulla}>Annulla</Button>
                <Button
                    className="btn btn-primary btn-add"
                    onClick={salva}
                    disabled={!sorgente || !area || salvataggio || salvaDisabilitato}
                >
                    {salvataggio ? 'Un attimo…' : 'Salva immagine'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ProductImageUploader;
